import { Content, ContentStack, TDocumentDefinitions } from "pdfmake/interfaces";

import { intoCareDocument } from "./document";
import { DocumentContext } from "./document-context";
import { numberedFooter } from "./footer";
import { card, descriptionItem, section, unbreakableSection } from "./generic";
import { reportHeader } from "./header";
import { shortTime } from "./utils";
import { RestrictiveMeasuresReportData } from "../reports/types";

const signatureSection = (ctx: DocumentContext): Content => ({
	columns: [
		{
			width: "30%",
			stack: [
				{ text: ".........................................", color: "gray" },
				{ text: ctx.t("features/pdf-generation.restrictiveMeasures.dateLabel") },
			],
		},
		{
			width: "*",
			stack: [
				{
					text: "...............................................................................................................",
					color: "gray",
				},
				{ text: ctx.t("features/pdf-generation.restrictiveMeasures.signatureLabel") },
			],
		},
	],
	margin: [0, 8, 0, 0],
});

const measureCard = (ctx: DocumentContext, item: RestrictiveMeasuresReportData["measures"][number]): ContentStack => {
	const defRows: [Content, Content][] = [
		descriptionItem({
			term: ctx.t("features/pdf-generation.restrictiveMeasures.detailsLabel"),
			content: item.data.measure.details ?? "-",
		}) as [Content, Content],
		...(item.data.measure.reason
			? ([
					descriptionItem({
						term: ctx.t("features/pdf-generation.restrictiveMeasures.reasonLabel"),
						content: item.data.measure.reason,
					}),
				] as [Content, Content][])
			: []),

		descriptionItem({
			term: ctx.t("features/pdf-generation.restrictiveMeasures.followUpPlanLabel"),
			content: item.data.followUpPlan.title,
		}) as [Content, Content],
		...(item.data.evaluation.evaluationDate
			? ([
					descriptionItem({
						term: ctx.t("features/pdf-generation.restrictiveMeasures.evaluationDateLabel"),
						content: new Date(item.data.evaluation.evaluationDate).toLocaleDateString(ctx.locale),
					}),
				] as [Content, Content][])
			: []),
	];

	return {
		stack: [
			card({
				content: {
					stack: [
						{
							text: item.measureTitle,
							bold: true,
							margin: [0, 0, 0, 6],
						},
						{
							table: {
								widths: ["auto", "*"],
								body: defRows,
							},
							layout: {
								defaultBorder: false,
								paddingTop: () => 0,
								paddingBottom: () => 0,
							},
						},
					],
				},
				options: { margins: [0, 8] },
			}),
		],
		unbreakable: true,
	};
};

export const getRestrictiveMeasuresDefinition = (
	ctx: DocumentContext,
	data: RestrictiveMeasuresReportData,
): TDocumentDefinitions => {
	const mainTitle =
		data.documentType === "ACTIVE"
			? ctx.t("features/pdf-generation.restrictiveMeasures.applicationApproval.mainTitle")
			: ctx.t("features/pdf-generation.restrictiveMeasures.discontinuationApproval.mainTitle");
	const doctorText =
		data.documentType === "ACTIVE"
			? ctx.t("features/pdf-generation.restrictiveMeasures.doctorSignature.applicationText")
			: ctx.t("features/pdf-generation.restrictiveMeasures.doctorSignature.discontinuationText");
	const residentText =
		data.documentType === "ACTIVE"
			? ctx.t("features/pdf-generation.restrictiveMeasures.residentSignature.applicationText")
			: ctx.t("features/pdf-generation.restrictiveMeasures.residentSignature.discontinuationText");

	const now = new Date();
	const createdByText = ctx.t("features/pdf-generation.restrictiveMeasures.header.createdBy", {
		date: now.toLocaleDateString(ctx.locale),
		time: now.toLocaleTimeString(ctx.locale, shortTime),
		user: data.user,
	});

	return intoCareDocument(ctx, {
		info: {
			title: `${data.resident.fullName} - ${mainTitle}`,
			author: "Corilus CareConnect",
		},
		content: [
			reportHeader({
				title: ctx.t("features/pdf-generation.restrictiveMeasures.header.smallTitle"),
				subTitle: `${data.resident.fullName} | ${mainTitle}`,
				topHeader: createdByText,
			}),
			section({
				title: "",
				content: { stack: data.measures.map((m) => measureCard(ctx, m)), marginBottom: 30 },
			}),
			unbreakableSection({
				title: ctx.t("features/pdf-generation.restrictiveMeasures.doctorSignature.title"),
				content: {
					stack: [{ text: doctorText }, signatureSection(ctx)],
					marginBottom: 30,
				},
			}),
			unbreakableSection({
				title: ctx.t("features/pdf-generation.restrictiveMeasures.residentSignature.title"),
				content: {
					stack: [{ text: residentText }, signatureSection(ctx)],
				},
			}),
		],
		footer: (currentPage: number, pageCount: number): Content => numberedFooter(ctx, currentPage, pageCount),
	});
};
